// AuditEventsTimeline — кастомный MD3-styled Timeline для отображения событий аудита.
// Используется на DashboardPage (RecentActivity) и /admin/audit-events.
// Реализация без @mui/lab (dependency-light).
// R41 / U-16: подписи типов событий через i18n (не сырые TASK_CREATED и т.п.);
// R41 / U-15: сырой entityId в тексте не показывается (остаётся в title-атрибуте);
// R41 / U-15: имя актора резолвится по userId через useUserMap (email-first цепочка,
// buildUserMap) — на проводе AuditDto содержит только userId (нет username).
import { Box, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { AuditEvent } from '@/api/audit';
import { useUserMap, resolveUserName } from '@/api/users';
import { t } from '@/i18n';
import { formatAuditType, formatAuditEntity } from '@/lib/format';

interface AuditEventsTimelineProps {
  /** Список событий аудита. */
  events: AuditEvent[];
}

const EVENT_DOT_COLORS: Record<string, string> = {
  TASK_CREATED: '#2BAC06', // Jivo primary
  TASK_UPDATED: '#F59E0B', // warning
  TASK_DELETED: '#EF4444', // error
  DEAL_CREATED: '#49DA1F', // Jivo secondary
  DEAL_UPDATED: '#F59E0B',
  DEAL_STAGE_CHANGED: '#49DA1F',
  EVENT_CREATED: '#2BAC06',
  USER_LOGIN: '#49DA1F',
};

/** Цвет точки Timeline по типу события. */
function dotColor(eventType: string): string {
  return EVENT_DOT_COLORS[eventType] ?? '#9CA3AF'; // grey по умолчанию
}

/** Кастомный MD3 Timeline для событий аудита. */
export function AuditEventsTimeline({ events }: AuditEventsTimelineProps): JSX.Element {
  // R41 / U-15: резолв акторов по userId всех видимых событий (lookup, ≤100 id на чанк).
  // Вызов ДО раннего return — rules of hooks; пустой список → пустая Map без запроса.
  const userMap = useUserMap(events.map((event) => event.userId));

  if (events.length === 0) {
    return (
      <Box data-testid="audit-events-timeline" sx={{ p: 2, color: 'text.secondary' }}>
        Нет событий
      </Box>
    );
  }

  return (
    <Box data-testid="audit-events-timeline">
      <Stack spacing={0}>
        {events.map((event, idx) => (
          <Stack
            key={event.id}
            direction="row"
            spacing={1.5}
            sx={{
              position: 'relative',
              pb: idx < events.length - 1 ? 2 : 0,
            }}
          >
            {/* Левая колонка: точка + вертикальная линия */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 16 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: dotColor(event.eventType),
                  mt: 0.5,
                  flexShrink: 0,
                }}
              />
              {idx < events.length - 1 ? (
                <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', mt: 0.5 }} />
              ) : null}
            </Box>
            {/* Правая колонка: контент */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatAuditType(event.eventType, t)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(new Date(event.createdAt), 'd MMM, HH:mm', { locale: ru })}
                </Typography>
              </Stack>
              {/* R41 / U-15: id сущности — только в title (не видимый текст),
                  имя актора — резолв по userId (email → username → displayName). */}
              <Typography variant="caption" color="text.secondary" component="div" title={event.entityId}>
                {formatAuditEntity(event.entityType, t)} · {resolveUserName(userMap, event.userId)}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
